// C:\Users\ADMIN\Desktop\ERP\myerp\my-frontend\src\pages\expense\service\ExpenseServiceNew.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./ExpenseServiceNew.module.css";
import api from "../../../api";
import { useTabActivated } from "../../../tabs/TabActivation";

import ExpenseItemPanel from "./ExpenseItemPanel";
import VendorPanel from "./VendorPanel";
import SalaryPanel from "./SalaryPanel";
import ConfirmPopup from "../../../components/ConfirmPopup";

export default function ExpenseServiceNew() {
  const navigate = useNavigate();

  const [draftDate, setDraftDate] = useState(() => toISO(new Date()));
  const [executionDate, setExecutionDate] = useState("");
  const executionDateInputRef = useRef(null);

  // ─── 인건비/일용직 인건비 빠른 입력 흐름 ───
  const [quickMode, setQuickMode] = useState(null); // null | "labor" | "daily"
  // null | "vendor-check" | "date-check" | "service-scope-check" | "account-mode-check"
  const [popupStep, setPopupStep] = useState(null);
  // 단계별 순차 공개 — 0: 아무것도 안 보임 / 1: 거래처확인 통과(기안번호·제목·
  // 사업구분) / 2: 날짜확인 통과(+작성일·집행예정일·용역명검색) / 3: 용역명
  // 선택됨(+구분명·내용명·분야명·거래처표·인건비명단표). 그 이후(내용추가유무·
  // 금액·거래방법·저장)는 인건비 명단에 1건 이상 입력되면 자동 공개(별도
  // flowStage 불필요 — salaryList.length로 직접 판단).
  const [flowStage, setFlowStage] = useState(0);
  // 단계 확정 필드 회색 처리 — 확정되면 readOnly(기존 .rowField input[readonly]
  // 회색 스타일 그대로 재사용) + 값 변경 불가. 작성일·집행예정일·용역명 3개
  // 필드만 [수정] 버튼으로 되돌릴 수 있음(다른 회색 필드는 [취소]로만 전체 초기화).
  const [datesConfirmed, setDatesConfirmed] = useState(false);
  const [serviceNameConfirmed, setServiceNameConfirmed] = useState(false);
  // 급여계좌 방식(재직자 전체/개별)을 이미 결정했는지 — 용역명을 [수정]으로
  // 재선택해도 이 팝업은 재실행하지 않기 위한 가드.
  const [accountModeDecided, setAccountModeDecided] = useState(false);
  // null | "salary-account" — 개별 선택 시 거래처 표 검색을 급여계좌만으로 필터
  const [vendorFilterMode, setVendorFilterMode] = useState(null);
  const [seq, setSeq] = useState("001");
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [remark, setRemark] = useState("");

  // DB 코드
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [fields, setFields] = useState([]);

  // 자동완성 선택값
  const [categoryLabel, setCategoryLabel] = useState("");
  const [categoryId, setCategoryId] = useState(null);

  const [itemLabel, setItemLabel] = useState("");
  const [itemId, setItemId] = useState(null);

  const [fieldLabel, setFieldLabel] = useState("");
  const [fieldId, setFieldId] = useState(null);

  // ─── 용역명 (거래처 목록에서 검색) ───
  const [serviceNameLabel, setServiceNameLabel] = useState("");
  const [serviceNameId, setServiceNameId]       = useState(null);
  const [showServiceNameList, setShowServiceNameList] = useState(false);
  const [serviceNameHighlightIdx, setServiceNameHighlightIdx] = useState(-1);
  const serviceNameListRef = useRef(null);
  const serviceNameRef     = useRef(null);

  // 드롭다운 ON/OFF
  const [showCategoryList, setShowCategoryList] = useState(false);
  const [showItemList, setShowItemList] = useState(false);
  const [showFieldList, setShowFieldList] = useState(false);

  // 강조 index
  const [catHighlightIdx, setCatHighlightIdx] = useState(-1);
  const [itemHighlightIdx, setItemHighlightIdx] = useState(-1);
  const [fieldHighlightIdx, setFieldHighlightIdx] = useState(-1);

  // 내용 추가 유무
  const [hasAdditional, setHasAdditional] = useState("없음");
  const [additionalCount, setAdditionalCount] = useState("");
  const [additionalUnit, setAdditionalUnit] = useState("종");

  // 거래처
  const [partners, setPartners] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [partnerList, setPartnerList] = useState([
    { name: "", id: null, show: false, highlight: -1 }
  ]);

  // 패널 데이터
  const [itemList, setItemList] = useState([]);
  const [salaryList, setSalaryList] = useState([]);

  // 드롭다운 항목 ref
  const catListRef   = useRef(null);
  const itemListRef  = useRef(null);
  const fieldListRef = useRef(null);
  const categoryRef  = useRef(null);
  const itemRef      = useRef(null);
  const fieldRef     = useRef(null);
  
  // ── 단축키용 ref ──
  const draftDateInputRef   = useRef(null);
  const serviceNameInputRef = useRef(null);
  const categoryInputRef    = useRef(null);
  const itemInputRef        = useRef(null);
  const fieldInputRef       = useRef(null);
  const vendorPanelRef      = useRef(null);
  const itemPanelRef        = useRef(null);
  const salaryPanelRef      = useRef(null);


  // itemList / salaryList 변경 시 총금액 자동 계산
  useEffect(() => {
    try {
      let supply = 0, vatSum = 0, salaryTotal = 0;
      itemList.forEach(it => {
        supply += Number(it.supply || it.supply_amount || 0);
        vatSum += Number(it.vat || 0);
      });
      salaryList.forEach(s => {
        salaryTotal += Number((s.totalSalary || "0").toString().replace(/,/g, ""));
      });
      setSupplyAmount(String(supply + salaryTotal));
      setVat(String(vatSum));
    } catch (err) {
      console.error("❌ 자동 합계 계산 오류:", err);
    }
  }, [itemList, salaryList]);

  // ✅ 인건비/일용직 인건비 빠른 입력 흐름에서만: 인건비 명단(salaryList) 인원수
  // 기준 "내용 추가 유무" 자동 갱신. 일반 진입(버튼 미사용)은 기존 동작 그대로
  // 보존하기 위해 quickMode가 아닐 때는 건드리지 않는다. 자동값일 뿐이라 이후
  // 수동 수정 가능.
  useEffect(() => {
    if (!quickMode) return;
    if (salaryList.length === 0) return;
    if (salaryList.length === 1) {
      setHasAdditional("없음");
    } else {
      setHasAdditional("있음");
      setAdditionalCount(String(salaryList.length));
      setAdditionalUnit("명");
    }
  }, [quickMode, salaryList.length]);

  const [supplyAmount, setSupplyAmount] = useState("");
  const [vat, setVat] = useState("");
  const totalAmount = useMemo(() => (Number(supplyAmount) || 0) + (Number(vat) || 0), [supplyAmount, vat]);

  const [payMethodLabel, setPayMethodLabel] = useState("계좌이체");
  const PAY_METHOD_LABELS = ["계좌이체", "카드결제", "현금결제"];
  const PAY_METHOD_TO_CODE = { "계좌이체": "transfer", "카드결제": "card", "현금결제": "cash" };

  // ── 스크롤 헬퍼 ──
  const scrollIntoView = (listRef, idx) => {
    if (!listRef.current) return;
    const els = listRef.current.querySelectorAll("[data-item]");
    if (els[idx]) els[idx].scrollIntoView({ block: "nearest" });
  };

  // ── 용역명 필터 ──
  // ✅ 인건비/일용직 인건비 빠른 입력 흐름에서는 "(용역)"이 포함된 거래처만 후보로
  const filteredServiceNames = useMemo(() => {
    const base = partners.filter(p => p.name.toLowerCase().includes(serviceNameLabel.toLowerCase()));
    return quickMode ? base.filter(p => p.name.includes("(용역)")) : base;
  }, [partners, serviceNameLabel, quickMode]);

  // ── 용역명 키보드 핸들러 ──
  const handleServiceNameKeyDown = (e) => {
    if (!showServiceNameList) {
      if (e.key === "ArrowDown") { setShowServiceNameList(true); setServiceNameHighlightIdx(0); }
      return;
    }
    if (e.key === "Escape") {
      setShowServiceNameList(false); setServiceNameHighlightIdx(-1);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setServiceNameHighlightIdx(prev => {
        const next = Math.min(prev + 1, filteredServiceNames.length - 1);
        scrollIntoView(serviceNameListRef, next); return next;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setServiceNameHighlightIdx(prev => {
        const next = Math.max(prev - 1, 0);
        scrollIntoView(serviceNameListRef, next); return next;
      });
    } else if (e.key === "Enter") {
      e.preventDefault();
      const p = filteredServiceNames[serviceNameHighlightIdx];
      if (p) {
        setServiceNameLabel(p.name); setServiceNameId(p.id);
        setShowServiceNameList(false); setServiceNameHighlightIdx(-1);
        applyQuickAutoFill(quickMode);
      }
    }
  };

  // ── 구분명 키보드 핸들러 ──
  const handleCategoryKeyDown = (e) => {
    if (!showCategoryList) { if (e.key === "ArrowDown") { setShowCategoryList(true); setCatHighlightIdx(0); } return; }
    if (e.key === "Escape") { setShowCategoryList(false); setCatHighlightIdx(-1); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setCatHighlightIdx(prev => { const n = Math.min(prev+1, filteredCategories.length-1); scrollIntoView(catListRef,n); return n; }); }
    else if (e.key === "ArrowUp")   { e.preventDefault(); setCatHighlightIdx(prev => { const n = Math.max(prev-1, 0); scrollIntoView(catListRef,n); return n; }); }
    else if (e.key === "Enter") { e.preventDefault(); const c=filteredCategories[catHighlightIdx]; if(c){setCategoryLabel(c.name);setCategoryId(c.id);setShowCategoryList(false);setCatHighlightIdx(-1);} }
  };

  // ── 내용명 키보드 핸들러 ──
  const handleItemKeyDown = (e) => {
    if (!showItemList) { if (e.key === "ArrowDown") { setShowItemList(true); setItemHighlightIdx(0); } return; }
    if (e.key === "Escape") { setShowItemList(false); setItemHighlightIdx(-1); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setItemHighlightIdx(prev => { const n=Math.min(prev+1,filteredItems.length-1); scrollIntoView(itemListRef,n); return n; }); }
    else if (e.key === "ArrowUp")   { e.preventDefault(); setItemHighlightIdx(prev => { const n=Math.max(prev-1,0); scrollIntoView(itemListRef,n); return n; }); }
    else if (e.key === "Enter") { e.preventDefault(); const it=filteredItems[itemHighlightIdx]; if(it){setItemLabel(it.name);setItemId(it.id);setShowItemList(false);setItemHighlightIdx(-1);} }
  };

  // ── 분야명 키보드 핸들러 ──
  const handleFieldKeyDown = (e) => {
    if (!showFieldList) { if (e.key === "ArrowDown") { setShowFieldList(true); setFieldHighlightIdx(0); } return; }
    if (e.key === "Escape") { setShowFieldList(false); setFieldHighlightIdx(-1); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setFieldHighlightIdx(prev => { const n=Math.min(prev+1,filteredFields.length-1); scrollIntoView(fieldListRef,n); return n; }); }
    else if (e.key === "ArrowUp")   { e.preventDefault(); setFieldHighlightIdx(prev => { const n=Math.max(prev-1,0); scrollIntoView(fieldListRef,n); return n; }); }
    else if (e.key === "Enter") { e.preventDefault(); const f=filteredFields[fieldHighlightIdx]; if(f){setFieldLabel(f.name);setFieldId(f.id);setShowFieldList(false);setFieldHighlightIdx(-1);} }
  };

  // DB LOAD
  useEffect(() => {
    (async () => {
      try {
        const [resCat, resItem, resField] = await Promise.all([
          api.get("/expense/usage/category/"),
          api.get("/expense/usage/item/"),
          api.get("/expense/usage/field/"),
        ]);
        setCategories(resCat.data);
        setItems(resItem.data);
        setFields(resField.data);

        setLoadingVendors(true);
        const resVendor = await api.get("vendors/");
        setPartners(Array.isArray(resVendor.data) ? resVendor.data : resVendor.data.results || []);
      } catch (err) {
        console.error("❌ 코드 불러오기 실패:", err);
      } finally {
        setLoadingVendors(false);
      }
    })();
  }, []);

  useEffect(() => { setExecutionDate(draftDate); }, [draftDate]);

  const yymmdd  = useMemo(() => draftDate.replaceAll("-", "").slice(2), [draftDate]);
  const draftNo = useMemo(() => `금천일자리(주)용역지출-${yymmdd}-${seq}호`, [yymmdd, seq]);

  // ✅ keep-alive 탭 재방문 시 다음 번호를 다시 조회(마운트가 유지돼 처음 조회값에 멈춰 있지 않게)
  const [seqRefreshTick, setSeqRefreshTick] = useState(0);
  useTabActivated(() => setSeqRefreshTick((t) => t + 1));
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const { data } = await api.get(`/expense/service/next-seq/?date=${draftDate}`);
        if (!ignore) setSeq(String(data.next_seq).padStart(3, "0"));
      } catch (err) { console.error("❌ next-seq 오류:", err); }
    })();
    return () => { ignore = true; };
  }, [draftDate, seqRefreshTick]);

  // 제목 자동 생성
  useEffect(() => {
    if (!executionDate || !categoryLabel || !itemLabel || !fieldLabel) return;
    const d = new Date(executionDate);
    const formatted = `${d.getFullYear()}년 ${String(d.getMonth()+1).padStart(2,"0")}월 ${String(d.getDate()).padStart(2,"0")}일`;
    let mid = `${categoryLabel}(${itemLabel}`;
    if (hasAdditional === "있음" && Number(additionalCount) > 0) mid += ` 외 ${additionalCount}${additionalUnit}`;
    mid += `)`;

    const cleanName = serviceNameLabel.replace(/\(용역\)/g, "").trim();
    setTitle(`${formatted} ${cleanName ? cleanName + " " : ""}${mid} ${fieldLabel}`);


  }, [executionDate, serviceNameLabel, categoryLabel, itemLabel, hasAdditional, additionalCount, additionalUnit, fieldLabel]);

  // ─── 인건비/일용직 인건비 빠른 입력 흐름 ───
  // ✅ 거래처 DB 정식 명칭(조회로 확인됨) — 아래 두 헬퍼가 참조
  const SERVICE_ALL_NAME = "용역 전체";
  const SALARY_ACCOUNT_ALL_NAME = "용역 현장근무자 급여계좌";

  const startQuickFlow = (mode) => {
    setQuickMode(mode);
    setPopupStep("vendor-check");
    setFlowStage(0);
    setDatesConfirmed(false);
    setServiceNameConfirmed(false);
    setAccountModeDecided(false);
    setVendorFilterMode(null);
  };

  // 흐름 중간에 취소하고 초기 화면(제목+버튼만)으로 복귀 — 이번 흐름에서
  // 채워진 값은 버리고 다음에 다시 깨끗하게 시작할 수 있도록 초기화한다.
  const cancelQuickFlow = () => {
    setQuickMode(null);
    setPopupStep(null);
    setFlowStage(0);
    setDatesConfirmed(false);
    setServiceNameConfirmed(false);
    setAccountModeDecided(false);
    setVendorFilterMode(null);
    setServiceNameLabel(""); setServiceNameId(null);
    setCategoryLabel(""); setCategoryId(null);
    setItemLabel(""); setItemId(null);
    setFieldLabel(""); setFieldId(null);
    setHasAdditional("없음"); setAdditionalCount("");
    setItemList([]); setSalaryList([]);
  };

  // Esc — 팝업이 떠 있을 때는 그 팝업 자체의 Esc(아니오)가 먼저 처리되므로,
  // 여기서는 팝업이 없을 때(흐름 진행 중 폼 입력 단계)만 초기 화면으로 되돌린다.
  useEffect(() => {
    if (!quickMode) return;
    const handleEscape = (e) => {
      if (e.key === "Escape" && !popupStep) {
        cancelQuickFlow();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [quickMode, popupStep]);

  const handleVendorCheckConfirm = () => {
    setPopupStep("date-check");
    setFlowStage(1);
  };
  const handleVendorCheckCancel = () => {
    setPopupStep(null);
    setQuickMode(null);
    navigate("/etc/vendors", { state: { defaultSalaryAccount: true } });
  };

  const handleDateCheckConfirm = () => {
    setPopupStep("service-scope-check");
    setFlowStage(2);
    setDatesConfirmed(true); // "예" — 오늘 날짜 그대로 확정, 바로 회색 읽기전용
  };
  const handleDateCheckCancel = () => {
    setPopupStep("service-scope-check");
    setFlowStage(2);
    // "아니오" — 아직 확정 아님(흰 배경 유지), 수정 후 필드에서 blur되면 확정
    executionDateInputRef.current?.focus();
  };

  // 날짜 필드에서 포커스가 빠져나갈 때(수정 완료로 간주) 확정 — "예"로 즉시
  // 확정되는 경로와 달리 "아니오"+수정 경로는 별도 확인 버튼이 없어 blur를
  // "수정 후 진행" 신호로 쓴다.
  const handleDateFieldBlur = () => {
    if (flowStage >= 2 && !datesConfirmed) setDatesConfirmed(true);
  };

  // ✅ [수정] 버튼 — 작성일·집행예정일·용역명 3개 필드 전용, 클릭 즉시(팝업 없이)
  // 흰 배경 편집 가능 상태로 복귀. 값 변경 후 확정되면 다시 회색(날짜는 blur,
  // 용역명은 재선택).
  const handleEditDates = () => {
    setDatesConfirmed(false);
    executionDateInputRef.current?.focus();
  };
  const handleEditServiceName = () => {
    // 용역명은 [수정] 시 전체/개별부터 다시 결정 — 급여계좌 팝업은 재실행 안 함
    // (accountModeDecided 유지), 거래처 값도 그대로 유지.
    setServiceNameConfirmed(false);
    setPopupStep("service-scope-check");
  };

  // ─── 용역 전체/개별 용역 분기 ───
  const handleServiceScopeAll = () => {
    const found = partners.find((p) => p.name === SERVICE_ALL_NAME);
    setPopupStep(null);
    if (!found) {
      alert(`거래처 DB에 '${SERVICE_ALL_NAME}'가 없습니다. 개별 검색으로 진행해주세요.`);
      return;
    }
    setServiceNameLabel(found.name);
    setServiceNameId(found.id);
    applyQuickAutoFill(quickMode);
  };
  const handleServiceScopeIndividual = () => {
    setPopupStep(null); // 검색창은 flowStage>=2로 이미 노출됨
  };

  // ─── 급여계좌 방식(재직자 전체/개별 선택) 분기 ───
  const handleAccountModeAll = () => {
    setPopupStep(null);
    setAccountModeDecided(true);
    const found = partners.find((p) => p.name === SALARY_ACCOUNT_ALL_NAME);
    if (found) {
      vendorPanelRef.current?.setFirstVendor({ name: found.name, id: found.id });
    } else {
      alert(`거래처 DB에 '${SALARY_ACCOUNT_ALL_NAME}'가 없습니다.`);
    }
  };
  const handleAccountModeIndividual = () => {
    setPopupStep(null);
    setAccountModeDecided(true);
    setVendorFilterMode("salary-account");
  };

  // 용역명 선택 직후 자동 채움 — 사용내역 구분명/내용명/분야명/거래방법.
  // DB에 해당 코드가 없으면(운영 데이터 변경 등) 조용히 건너뛴다 — 값이 없는데도
  // 채운 것처럼 보이면 더 혼란스럽기 때문.
  const applyQuickAutoFill = (mode) => {
    if (!mode) return;
    setFlowStage(3);
    setServiceNameConfirmed(true);
    const categoryName = mode === "labor" ? "급여" : "일용직 급여";
    const cat = categories.find((c) => c.name === categoryName);
    if (cat) { setCategoryLabel(cat.name); setCategoryId(cat.id); }

    const it = items.find((i) => i.name === "용역 현장근무자 급여");
    if (it) { setItemLabel(it.name); setItemId(it.id); }

    const fld = fields.find((f) => f.name === "지급");
    if (fld) { setFieldLabel(fld.name); setFieldId(fld.id); }

    setPayMethodLabel("계좌이체");

    // ✅ 용역명이 처음 확정될 때만 급여계좌 방식 팝업 — [수정]으로 재선택해도
    // accountModeDecided가 이미 true면 다시 뜨지 않는다(거래처 값 그대로 유지).
    if (!accountModeDecided) {
      setPopupStep("account-mode-check");
    }
  };

  // ── 저장 ──

  async function onSubmit(e) {
    e.preventDefault();
    if (saving) return;

    if (!executionDate) return alert("집행예정일을 입력해주세요.");

    // 용역명 유효성 검사
    if (serviceNameLabel.trim()) {
      const matched = partners.find(p => p.name.trim() === serviceNameLabel.trim());
      if (!matched) {
        alert(`용역명 거래처가 미등록 된 거래처입니다.\n\n입력값: "${serviceNameLabel}"\n\n거래처 목록에서 선택해주세요.`);
        return;
      }
    }


    const validVendor = partnerList.some(v => v.name.trim() !== "");
    if (!validVendor) return alert("최소 1개 이상의 거래처가 필요합니다.");
    if (!supplyAmount || Number(supplyAmount) === 0) return alert("공급가를 입력해주세요.");

    setSaving(true);

    const cleanedPartners = partnerList
      .filter(p => p.name.trim() !== "")
      .map(p => ({ name: p.name, id: p.id }));

    const payload = {
      draft_date:      draftDate,
      execution_date:  executionDate,


      service_name:    serviceNameLabel.trim() || null,
      service_name_id: serviceNameId || null,
      service_type:    serviceNameLabel.trim() || "",
      partner_name:    partnerList[0]?.name || "",


      partner_id:      partnerList[0]?.id || null,
      partners:        cleanedPartners,
      items:           itemList,
      salaries:        salaryList,
      category_name:   categoryLabel,
      item_name:       itemLabel,
      field_name:      fieldLabel,
      other_count:     hasAdditional === "있음" ? Number(additionalCount) || 0 : 0,
      title,
      supply_amount:   Number(supplyAmount) || 0,
      vat:             Number(vat) || 0,
      pay_method:      PAY_METHOD_TO_CODE[payMethodLabel] || "transfer",
      remark,
    };

    try {
      const { data } = await api.post("expense/service/", payload);
      setSavedId(data.id);
      alert(`✅ 저장 완료!\n기안번호: ${data.draft_no}`);

      const next = await api.get(`/expense/service/next-seq/?date=${draftDate}`);
      setSeq(String(next.data.next_seq).padStart(3, "0"));

      // 초기화
      setExecutionDate("");
      setServiceNameLabel(""); setServiceNameId(null);
      setCategoryLabel(""); setCategoryId(null);
      setItemLabel(""); setItemId(null);
      setFieldLabel(""); setFieldId(null);
      setPartnerList([{ name: "", id: null, show: false, highlight: -1 }]);
      setSupplyAmount(""); setVat(""); setRemark(""); setTitle("");
      setItemList([]); setSalaryList([]);
      setHasAdditional("없음"); setAdditionalCount("");
    } catch (err) {
      console.error("❌ 저장 실패:", err.response?.data || err);
      alert("서버 저장 실패: " + JSON.stringify(err.response?.data || err));
    } finally {
      setSaving(false);
    }
  }

  const filteredCategories = categories.filter(c => c.name.toLowerCase().includes(categoryLabel.toLowerCase()));
  const filteredItems      = items.filter(i => i.name.toLowerCase().includes(itemLabel.toLowerCase()));
  const filteredFields     = fields.filter(f => f.name.toLowerCase().includes(fieldLabel.toLowerCase()));

  // 용역명 불일치 경고
  const serviceNameInvalid =
    serviceNameLabel.trim().length > 0 &&
    !partners.some(p => p.name.trim() === serviceNameLabel.trim());

  return (
    <div className={styles.wrap}>
      {/* ✅ 항상 표시 — 버튼 미클릭 상태(quickMode=null)에서는 이 줄만 보인다 */}
      <div className={styles.headerBar}>
        <h3 className={styles.headerTitle}>(구) 용역사업 지출데이터 입력</h3>
        {!quickMode ? (
          <>
            {/* ⚠ Login/Signup 등 다른 .module.css의 전역 `button{width:100%}` 요소
                선택자가 CSS Modules에서 스코프 안 되고 앱 전역으로 새는 이슈 —
                width를 명시하지 않으면 이 leak에 잡혀 버튼이 화면 가득 늘어난다.
                width/flexShrink 반드시 명시(padding만으로는 못 이김). */}
            <button
              type="button"
              onClick={() => startQuickFlow("labor")}
              style={{
                width: "96px", flexShrink: 0, height: "28px", whiteSpace: "nowrap",
                border: "1px solid #2563eb", background: "#eff6ff", color: "#2563eb",
                borderRadius: "5px", fontSize: "13px", fontWeight: 600, cursor: "pointer",
                boxSizing: "border-box",
              }}
            >
              인건비
            </button>
            <button
              type="button"
              onClick={() => startQuickFlow("daily")}
              style={{
                width: "132px", flexShrink: 0, height: "28px", whiteSpace: "nowrap",
                border: "1px solid #7c3aed", background: "#f5f3ff", color: "#7c3aed",
                borderRadius: "5px", fontSize: "13px", fontWeight: 600, cursor: "pointer",
                boxSizing: "border-box",
              }}
            >
              일용직 인건비
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={cancelQuickFlow}
            style={{
              width: "84px", flexShrink: 0, height: "28px", whiteSpace: "nowrap",
              border: "1px solid #d1d5db", background: "#ffffff", color: "#374151",
              borderRadius: "5px", fontSize: "13px", fontWeight: 600, cursor: "pointer",
              boxSizing: "border-box", marginLeft: "auto",
            }}
          >
            취소
          </button>
        )}
      </div>

      {quickMode && (
      <div style={{ display: "flex", flexDirection: "row", width: "100%", gap: "0px" }}>

        <div style={{ flex: "0 0 10%" }}>
          <div className={styles.pane}>
            <form className={styles.form} onSubmit={onSubmit}>
              {/* 1단계: 거래처확인 통과 후 공개 */}
              {flowStage >= 1 && (
                <>
                  <Row label="기안번호(자동완성)">
                    <input value={draftNo} readOnly />
                  </Row>
                  <Row label="제목(자동완성)">
                    <input value={title} readOnly placeholder="자동 생성됩니다." />
                  </Row>
                  <Row label="사업구분">
                    <input value="용역" readOnly />
                  </Row>
                </>
              )}
              {/* 2단계: 날짜확인 통과 후 공개 */}
              {flowStage >= 2 && (
                <>
                  <Row label="작성일">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <input
                        ref={draftDateInputRef}
                        type="date"
                        value={draftDate}
                        onChange={e => setDraftDate(e.target.value)}
                        readOnly={datesConfirmed}
                        onBlur={handleDateFieldBlur}
                        style={{ flex: "1 1 auto", minWidth: 0 }}
                      />
                      {datesConfirmed && (
                        <button type="button" onClick={handleEditDates} className={styles.editSmallBtn}>수정</button>
                      )}
                    </div>
                  </Row>
                  <Row label="집행예정일">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <input
                        ref={executionDateInputRef}
                        type="date"
                        value={executionDate}
                        onChange={e => setExecutionDate(e.target.value)}
                        readOnly={datesConfirmed}
                        onBlur={handleDateFieldBlur}
                        style={{ flex: "1 1 auto", minWidth: 0 }}
                      />
                      {datesConfirmed && (
                        <button type="button" onClick={handleEditDates} className={styles.editSmallBtn}>수정</button>
                      )}
                    </div>
                  </Row>
                </>
              )}

              {/* ════════ 용역명 (2단계: 날짜확인 통과 후 공개) ════════ */}
              {flowStage >= 2 && (
              <Row label="용역명">
                <div style={{ display: "flex", alignItems: "flex-start" }}>
                <div className={styles.partnerRow} ref={serviceNameRef} style={{ flex: "1 1 auto", minWidth: 0 }}>
                  <div style={{ position: "relative" }}>
                    <input
                      ref={serviceNameInputRef}
                      placeholder="거래처 목록에서 검색"
                      value={serviceNameLabel}
                      readOnly={serviceNameConfirmed}
                      onChange={e => {
                        setServiceNameLabel(e.target.value);
                        setServiceNameId(null);
                        setShowServiceNameList(true);
                        setServiceNameHighlightIdx(-1);
                      }}
                      onFocus={() => {
                        if (serviceNameConfirmed) return;
                        setShowServiceNameList(true); setServiceNameHighlightIdx(-1);
                      }}
                      onBlur={() => setTimeout(() => setShowServiceNameList(false), 150)}
                      onKeyDown={handleServiceNameKeyDown}
                      style={serviceNameConfirmed ? {} : serviceNameInvalid
                        ? { borderColor: "#ef4444", background: "#fff5f5" }
                        : serviceNameId
                          ? { borderColor: "#10b981", background: "#f0fdf4" }
                          : {}
                      }
                    />
                    {!serviceNameConfirmed && (
                      <button
                        type="button"
                        className={styles.partnerButton}
                        onClick={() => setShowServiceNameList(v => !v)}
                      >▼</button>
                    )}
                  </div>

                  {/* 불일치 경고 문구 */}
                  {serviceNameInvalid && (
                    <div style={{ color: "#ef4444", fontSize: 12, marginTop: 3, lineHeight: 1.4 }}>
                      ⚠ 미등록 거래처입니다. 목록에서 선택해주세요.
                    </div>
                  )}

                  {showServiceNameList && (
                    <div className={styles.partnerBox} ref={serviceNameListRef}>
                      {filteredServiceNames.length > 0
                        ? filteredServiceNames.map((p, i) => (
                          <div
                            key={p.id}
                            data-item
                            className={`${styles.partnerItem} ${i === serviceNameHighlightIdx ? styles.activeItem : ""}`}
                            onMouseEnter={() => setServiceNameHighlightIdx(i)}
                            onMouseDown={e => e.preventDefault()} // onBlur 방지
                            onClick={() => {
                              setServiceNameLabel(p.name);
                              setServiceNameId(p.id);
                              setShowServiceNameList(false);
                              setServiceNameHighlightIdx(-1);
                              applyQuickAutoFill(quickMode);
                            }}
                          >
                            {p.name}
                          </div>
                        ))
                        : (
                          <div style={{ padding: "8px 12px", color: "#9ca3af" }}>
                            {loadingVendors ? "불러오는 중…" : "일치하는 거래처 없음"}
                          </div>
                        )
                      }
                    </div>
                  )}
                </div>
                {serviceNameConfirmed && (
                  <button type="button" onClick={handleEditServiceName} className={styles.editSmallBtn}>수정</button>
                )}
                </div>
              </Row>
              )}
              {/* ════════════════════════ */}

              {/* 구분명 (3단계: 용역명 선택 후 공개) */}
              {flowStage >= 3 && (
              <>
              <Row label="사용내역 구분명">
                <div className={styles.partnerRow} ref={categoryRef}>
                  <div style={{ position: "relative" }}>
                    <input
                      ref={categoryInputRef}
                      placeholder="주재료, 소모품 등" value={categoryLabel}
                      readOnly={serviceNameConfirmed}
                      onChange={e => { setCategoryLabel(e.target.value); setShowCategoryList(true); setCatHighlightIdx(-1); }}
                      onFocus={() => { if (serviceNameConfirmed) return; setShowCategoryList(true); setCatHighlightIdx(-1); }}
                      onKeyDown={handleCategoryKeyDown}
                    />
                    {!serviceNameConfirmed && (
                      <button type="button" className={styles.partnerButton} onClick={() => setShowCategoryList(v=>!v)}>▼</button>
                    )}
                  </div>
                  {showCategoryList && (
                    <div className={styles.partnerBox} ref={catListRef}>
                      {filteredCategories.length
                        ? filteredCategories.map((c,i) => (
                          <div key={c.id} data-item
                            className={`${styles.partnerItem} ${i===catHighlightIdx?styles.activeItem:""}`}
                            onMouseEnter={() => setCatHighlightIdx(i)}
                            onClick={() => { console.log("카테고리 선택:", c.id, c.name); setCategoryLabel(c.name); setCategoryId(c.id); setShowCategoryList(false); setCatHighlightIdx(-1); }}


                          >{c.name}</div>
                        ))
                        : <div style={{ padding:"8px 12px", color:"#9ca3af" }}>일치 없음</div>
                      }
                    </div>
                  )}
                </div>
              </Row>

              {/* 내용명 */}
              <Row label="사용내역 내용명">
                <div className={styles.partnerRow} ref={itemRef}>
                  <div style={{ position: "relative" }}>
                    <input
                      ref={itemInputRef}
                      placeholder="물품/용역명 입력" value={itemLabel}
                      readOnly={serviceNameConfirmed}
                      onChange={e => { setItemLabel(e.target.value); setShowItemList(true); setItemHighlightIdx(-1); }}
                      onFocus={() => { if (serviceNameConfirmed) return; setShowItemList(true); setItemHighlightIdx(-1); }}
                      onKeyDown={handleItemKeyDown}
                    />
                    {!serviceNameConfirmed && (
                      <button type="button" className={styles.partnerButton} onClick={() => setShowItemList(v=>!v)}>▼</button>
                    )}
                  </div>
                  {showItemList && (
                    <div className={styles.partnerBox} ref={itemListRef}>
                      {filteredItems.length
                        ? filteredItems.map((it,i) => (
                          <div key={it.id} data-item
                            className={`${styles.partnerItem} ${i===itemHighlightIdx?styles.activeItem:""}`}
                            onMouseEnter={() => setItemHighlightIdx(i)}
                            onClick={() => { setItemLabel(it.name); setItemId(it.id); setShowItemList(false); setItemHighlightIdx(-1); }}
                          >{it.name}</div>
                        ))
                        : <div style={{ padding:"8px 12px", color:"#9ca3af" }}>일치 없음</div>
                      }
                    </div>
                  )}
                </div>
              </Row>
              </>
              )}

              {/* 내용 추가 유무 — 인건비 명단에 1건 이상 입력된 뒤 공개 */}
              {flowStage >= 3 && salaryList.length > 0 && (
              <Row label="내용 추가 유무">
                <div className={styles.inline}>
                  <label className={styles.check}><input type="checkbox" checked={hasAdditional==="없음"} onChange={()=>setHasAdditional("없음")} /> 없음</label>
                  <label className={styles.check}><input type="checkbox" checked={hasAdditional==="있음"} onChange={()=>setHasAdditional("있음")} /> 있음</label>
                  <input type="number" value={additionalCount} onChange={e=>setAdditionalCount(e.target.value)}
                    placeholder="숫자" disabled={hasAdditional==="없음"} style={{width:"60px"}} />
                  <label className={styles.check}><input type="checkbox" checked={additionalUnit==="종"} onChange={()=>setAdditionalUnit("종")} disabled={hasAdditional==="없음"} />종</label>
                  <label className={styles.check}><input type="checkbox" checked={additionalUnit==="명"} onChange={()=>setAdditionalUnit("명")} disabled={hasAdditional==="없음"} />명</label>
                </div>
              </Row>
              )}

              {/* 분야명 (3단계: 용역명 선택 후 공개 — 구분명·내용명과 같은 그룹) */}
              {flowStage >= 3 && (
              <Row label="사용내역 분야명">
                <div className={styles.partnerRow} ref={fieldRef}>
                  <div style={{ position: "relative" }}>
                    <input
                      ref={fieldInputRef}
                      placeholder="구입, 결제, 지급 등" value={fieldLabel}
                      readOnly={serviceNameConfirmed}
                      onChange={e => { setFieldLabel(e.target.value); setShowFieldList(true); setFieldHighlightIdx(-1); }}
                      onFocus={() => { if (serviceNameConfirmed) return; setShowFieldList(true); setFieldHighlightIdx(-1); }}
                      onKeyDown={handleFieldKeyDown}
                    />
                    {!serviceNameConfirmed && (
                      <button type="button" className={styles.partnerButton} onClick={() => setShowFieldList(v=>!v)}>▼</button>
                    )}
                  </div>
                  {showFieldList && (
                    <div className={styles.partnerBox} ref={fieldListRef}>
                      {filteredFields.length
                        ? filteredFields.map((f,i) => (
                          <div key={f.id} data-item
                            className={`${styles.partnerItem} ${i===fieldHighlightIdx?styles.activeItem:""}`}
                            onMouseEnter={() => setFieldHighlightIdx(i)}
                            onClick={() => { setFieldLabel(f.name); setFieldId(f.id); setShowFieldList(false); setFieldHighlightIdx(-1); }}
                          >{f.name}</div>
                        ))
                        : <div style={{ padding:"8px 12px", color:"#9ca3af" }}>일치 없음</div>
                      }
                    </div>
                  )}
                </div>
              </Row>
              )}

              {/* 금액~저장버튼: 인건비 명단에 1건 이상 입력된 뒤 공개 */}
              {flowStage >= 3 && salaryList.length > 0 && (
              <>
              {/* 금액 */}
              <Row label="금액(자동완성)">
                <div className={styles.amountBox}>
                  <input
                    value={supplyAmount.toString().replace(/\B(?=(\d{3})+(?!\d))/g,",")}
                    onChange={e => { const r=e.target.value.replace(/,/g,""); if(!isNaN(r)) setSupplyAmount(r); }}
                    placeholder="공급가"
                  />
                  +
                  <input
                    value={vat.toString().replace(/\B(?=(\d{3})+(?!\d))/g,",")}
                    onChange={e => { const r=e.target.value.replace(/,/g,""); if(!isNaN(r)) setVat(r); }}
                    placeholder="부가세"
                  />
                  =
                  <input readOnly value={totalAmount.toString().replace(/\B(?=(\d{3})+(?!\d))/g,",")} placeholder="합계" />
                </div>
              </Row>

              {/* 거래방법 */}
              <Row label="거래방법">
                <div className={styles.inline}>
                  {PAY_METHOD_LABELS.map(label => (
                    <label key={label} className={styles.check}>
                      <input type="checkbox" checked={payMethodLabel===label} onChange={()=>setPayMethodLabel(label)} /> {label}
                    </label>
                  ))}
                </div>
              </Row>

              {/* 비고 */}
              <Row label="비고">
                <textarea rows={1} value={remark} onChange={e=>setRemark(e.target.value)} placeholder="지출 메모 입력" />
              </Row>

              <div className={styles.actions}>
                <button type="submit" disabled={saving} className={styles.primaryBtn}>
                  {saving ? "저장 중..." : "저장"}
                </button>
              </div>
              </>
              )}
            </form>
          </div>
        </div>

        {/* 오른쪽 패널 — 5,6단계: 용역명 선택 후 공개(거래처 표 → 인건비 명단 표) */}
        <div style={{ flex: "1", padding: "0px" }}>

          {flowStage >= 3 && (
            <VendorPanel
              ref={vendorPanelRef}
              vendors={partners}
              loadingVendors={loadingVendors}
              onChange={list => setPartnerList(list)}
              filterPredicate={vendorFilterMode === "salary-account" ? (v => !!v.is_salary_account) : undefined}
            />
          )}
          {/* ✅ 인건비/일용직 인건비 빠른 입력에서는 품목 테이블 숨김 — 일반 진입 시엔 그대로 표시 */}
          {!quickMode && <ExpenseItemPanel ref={itemPanelRef} onItemsChange={setItemList} />}
          {flowStage >= 3 && (
            <SalaryPanel
              ref={salaryPanelRef}
              onItemsChange={setSalaryList}
              vendors={partners}
              mode={quickMode}
              draftDate={draftDate}
              prefillServiceName={quickMode ? { name: serviceNameLabel, id: serviceNameId } : null}
            />
          )}


        </div>

      </div>
      )}

      {popupStep === "vendor-check" && (
        <ConfirmPopup
          message={"거래처(급여계좌)가 등록되어 있습니까?"}
          onConfirm={handleVendorCheckConfirm}
          onCancel={handleVendorCheckCancel}
        />
      )}
      {popupStep === "date-check" && (
        <ConfirmPopup
          message={`작성일: ${draftDate}\n집행예정일: ${executionDate}\n\n오늘 날짜로 진행하시겠습니까?`}
          onConfirm={handleDateCheckConfirm}
          onCancel={handleDateCheckCancel}
        />
      )}
      {popupStep === "service-scope-check" && (
        <ConfirmPopup
          message={"용역명은 '용역 전체'인가요? 아니면 용역별인가요?"}
          confirmLabel="용역 전체"
          cancelLabel="개별 용역"
          onConfirm={handleServiceScopeAll}
          onCancel={handleServiceScopeIndividual}
        />
      )}
      {popupStep === "account-mode-check" && (
        <ConfirmPopup
          message={"거래처를 '용역 현장근무자 급여계좌' 전체로 하시겠습니까, 개별 급여계좌로 하시겠습니까?"}
          confirmLabel="재직자 전체"
          cancelLabel="개별 선택"
          onConfirm={handleAccountModeAll}
          onCancel={handleAccountModeIndividual}
        />
      )}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className={styles.row}>
      <div className={styles.rowLabel}>{label}</div>
      <div className={styles.rowField}>{children}</div>
    </div>
  );
}

function toISO(d) {
  const pad = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
}